import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Grid, Paper, Typography, Card, CardContent, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import moment from 'moment';
import { Pie, Bar } from 'react-chartjs-2';
import { useTable } from 'react-table';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { renderAddressMapLink } from './reportMapLinks';

const useStyles = makeStyles((theme) => ({
    label: {
        backgroundColor: 'RoyalBlue',
        color: 'White',
        borderBottomColor: 'MidnightBlue',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
    },
    value: {
        backgroundColor: 'white',
        borderBottomColor: 'MidnightBlue',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
    },
    table: {
        minWidth: 700,
    },

}));

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: 'Silver',
        color: useTheme().palette.common.black,
        fontSize: 13,
        fontStyle: 'bold'
    },
    body: {
        fontSize: 13,
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: useTheme().palette.action.hover,
        },
    },
}))(TableRow);


const pieOptions = (title) => {
    return {
        responsive: true,
        plugins: {
            legend: {
                dispay: true,
                position: 'bottom',
                labels: {
                    fontSize: 12,
                    fontStyle: 'bold'
                }
            },
            title: {
                display: true,
                text: title,
                position: 'top',
                labels: {
                    fontSize: 12,
                    fontStyle: 'bold'
                }
            },
        }
    }
};

const barOptions = (title) => {
    return {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: title,
                position: 'top',
                labels: {
                    fontSize: 12,
                    fontStyle: 'bold'
                }
            },
        }
    }
};

const BinarySensor = ({ reportData }) => {
    const classes = useStyles();
    const dimentions = useSelector(liveTrackinglayoutSelector);
    const {reportName} = reportData;
    const [timePie, setTimePie] = useState(null);
    const [distPie, setDistPie] = useState(null);
    const [multiSensor, setMultiSensor] = useState(false);

    const tableData = useMemo(() => {
        if (reportData.reportRecords && reportData.reportRecords.length > 0) {
            return reportData.reportRecords.map(item => {
                const startPoint = reportData?.track?.[item.startIndex];
                const endPoint = reportData?.track?.[item.endIndex];
                let rowObj = {
                    start: moment(item.startTime).format('D/M/Y hh:mm A'),
                    end: moment(item.endTime).format('D/M/Y hh:mm A'),
                    duration: item.activeDuration,
                    distance: `${item.distance.toFixed(2)} Km`,
                    startLocation: renderAddressMapLink({
                        address: item.startAddress,
                        lat: startPoint?.lat,
                        lng: startPoint?.lng,
                        source: item,
                        type: 'start',
                    }),
                    endLocation: renderAddressMapLink({
                        address: item.endAddress,
                        lat: endPoint?.lat,
                        lng: endPoint?.lng,
                        source: item,
                        type: 'end',
                    }),
                    key: item.startTime
                };
                if (multiSensor) {
                    rowObj = { ...rowObj, sensor: item.eventName };
                }
                return rowObj;
            })
        } else {
            return [];
        }
    }, [reportData, multiSensor]);

    const tableColumns = useMemo(() => {
        let colObj = multiSensor ? [{ Header: 'Sensor', accessor: 'sensor' }] : [];
        colObj = [
            ...colObj,
            { Header: reportName && reportName.indexOf("Wire") != -1 ? "Cut-Off" : "Start", accessor: 'start' },
            { Header: reportName && reportName.indexOf("Wire") != -1 ? "Good" : 'End', accessor: 'end' },
            { Header: 'Duration', accessor: 'duration' },
            { Header: 'Distance', accessor: 'distance' },
            { Header: 'Start Location', accessor: 'startLocation' },
            { Header: 'End Location', accessor: 'endLocation' },
        ];
        return colObj;
    }, [multiSensor]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns: tableColumns, data: tableData });


    /*useEffect(() => {
        //Object.keys(reportData).forEach(key => console.log("Key", key, "Val", reportData[key]))

    }, [reportData])*/

    useEffect(() => {
        if (reportData.dataTable) {
            let dataTime = [];
            let dataDist = [];
            let dataColor = ['DodgerBlue', 'DarkGray'];
            let pieLabels = ['Active', 'Inactive'];
            let barlabels = ['Active', 'Inactive'];
            let multi = false;

            if (Object.keys(reportData.meta.activeHours).length > 2) {
                dataTime = [...Object.keys(reportData.meta.activeHours).filter(k => k !== 'Total').map(k => Math.ceil(reportData.meta.activeHours[k] / reportData.meta.totalHours * 100)), Math.ceil(reportData.meta.inactiveHours / reportData.meta.totalHours * 100)];
                pieLabels = [...Object.keys(reportData.meta.activeHours).filter(k => k !== 'Total').map(k => `${k}`), 'Inactive'];
                dataColor = ['DodgerBlue', 'RoyalBlue', 'DarkGray'];
                multi = true;
            } else {
                dataTime = [Math.ceil(reportData.meta.activeHours.Total / reportData.meta.totalHours * 100), Math.ceil(reportData.meta.inactiveHours / reportData.meta.totalHours * 100)];
            }

            if (Object.keys(reportData.meta.activeDistance).length > 2) {
                dataDist = [...Object.keys(reportData.meta.activeDistance).filter(k => k !== 'Total').map(k => reportData.meta.activeDistance[k].toFixed(2)), reportData.meta.inactiveDistance.toFixed(2)];
                barlabels = [...Object.keys(reportData.meta.activeHours).filter(k => k !== 'Total').map(k => `${k}`), 'Inactive'];
            } else {
                dataDist = [reportData.meta.activeDistance.Total.toFixed(2), reportData.meta.inactiveDistance.toFixed(2)];
            }
            setTimePie(
                {
                    labels: pieLabels,
                    datasets: [{
                        label: 'Duration',
                        data: dataTime,
                        backgroundColor: dataColor,
                        borderColor: dataColor,
                        hoverOffset: 4
                    }]
                }
            );
            setDistPie({
                labels: barlabels,
                datasets: [{
                    label: 'Kilometers',
                    data: dataDist,
                    backgroundColor: dataColor,
                    borderColor: dataColor,
                    hoverOffset: 4,
                    barThickness: 40,
                }]
            })
            setMultiSensor(multi);
        }
        //console.log("Binary", reportData);
    }, [reportData]);

    if (reportData.dataTable && reportData.reportRecords && reportData.reportRecords.length > 0) {
        //console.log("Rendering", reportData.dataTable, reportData.reportRecords.length);
        return (
            <Paper style={{ ...dimentions.reportDetailViewBottom, overflow: 'auto' }}>
                <div style={{ margin: '10px', marginRight: '5px' }}>
                    <Grid container spacing={1}>
                        <Grid
                            item
                            container
                            spacing={2}
                            sm={12}
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="stretch"
                        >
                            <Grid item container sm={6}>
                                <Card style={{ backgroundColor: 'White', width: '100%' }} variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" style={{ color: 'RoyalBlue' }}>Report Summary</Typography>
                                        <Divider />
                                        <br />
                                        <Grid container>
                                            <Grid item sm={2}>
                                                <Typography gutterBottom variant="body1" style={{ color: 'Navy' }}>
                                                    From
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={10}>
                                                <Typography gutterBottom variant="body1" style={{ color: 'Navy' }}>
                                                    {`${moment(reportData.reqBody.timeFrom).format('Do MMM YY hh:mm A')}`}
                                                </Typography>
                                                <Typography gutterBottom variant="body2" style={{ color: 'RoyalBlue' }}>
                                                    {renderAddressMapLink({
                                                        address: reportData.reportRecords[0].startAddress,
                                                        lat: reportData?.track?.[reportData.reportRecords[0].startIndex]?.lat,
                                                        lng: reportData?.track?.[reportData.reportRecords[0].startIndex]?.lng,
                                                        source: reportData.reportRecords[0],
                                                        type: 'start',
                                                    })}
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={2}>
                                                <Typography gutterBottom variant="body1" style={{ color: 'Navy' }}>
                                                    To
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={10}>
                                                <Typography gutterBottom variant="body1" style={{ color: 'Navy' }}>
                                                    {`${moment(reportData.reqBody.timeTo).format('Do MMM YY hh:mm A')}`}
                                                </Typography>
                                                <Typography gutterBottom variant="body2" style={{ color: 'RoyalBlue' }}>
                                                    {renderAddressMapLink({
                                                        address: reportData.reportRecords[reportData.reportRecords.length - 1].endAddress,
                                                        lat: reportData?.track?.[reportData.reportRecords[reportData.reportRecords.length - 1].endIndex]?.lat,
                                                        lng: reportData?.track?.[reportData.reportRecords[reportData.reportRecords.length - 1].endIndex]?.lng,
                                                        source: reportData.reportRecords[reportData.reportRecords.length - 1],
                                                        type: 'end',
                                                    })}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item container sm={3}>
                                {
                                    reportData.reportName !== "Engine" && distPie &&
                                    <Grid item sm={12}>
                                        <Bar
                                            data={distPie}
                                            height={200}
                                            width={200}
                                            options={{
                                                ...barOptions('Distance Breakup (Km)'), maintainAspectRatio: false, animation: { duration: 0 }
                                            }}
                                        />
                                    </Grid>
                                }
                            </Grid>
                            <Grid item container sm={3}>
                                {
                                    timePie && 
                                    <Grid item sm={12}>
                                        <Pie
                                            data={timePie}
                                            height={200}
                                            width={200}
                                            options={{ ...pieOptions('Duration Breakup (% Time)'), maintainAspectRatio: false, animation: { duration: 0 } }}
                                        />
                                    </Grid>
                                }

                            </Grid>
                            <Grid item container sm={multiSensor ? 3 : 6}>
                                <Card style={{ width: '100%', borderColor: 'SaddleBrown', borderStyle: 'solid', borderWidth: '1px' }}>
                                    <CardContent>
                                        <Typography variant="h5" style={{ color: 'SaddleBrown' }}>Route Details</Typography>
                                        <Divider style={{ background: 'SaddleBrown' }} />
                                        <br />
                                        <Grid container spacing={1}>
                                            <Grid item sm={4}>
                                                <Typography gutterBottom variant="body1" style={{ color: 'SaddleBrown', fontSize: 14 }}>
                                                    Distance
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={8}>
                                                <Typography gutterBottom variant="h6" style={{ color: 'SaddleBrown' }}>
                                                    {`${reportData.meta.totalDistance.toFixed(2)} Km`}
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={4}>
                                                <Typography gutterBottom variant="body1" style={{ color: 'SaddleBrown', fontSize: 14 }}>
                                                    {reportName && reportName.indexOf("Wire") != -1 ? "Cut Off " : "Active "}Duration
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={8}>
                                                <Typography gutterBottom variant="h6" style={{ color: 'SaddleBrown' }}>
                                                    {`${reportData.meta.totalDuration}`}
                                                </Typography>
                                            </Grid>
                                        </Grid>


                                    </CardContent>
                                </Card>
                            </Grid>
                            {
                                multiSensor ?
                                    Object.keys(reportData.meta.activeHours).map((key, index) =>
                                        <Grid item container sm={3}>
                                            <Card style={{
                                                width: '100%', borderColor: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue',
                                                borderStyle: 'solid', borderWidth: '1px'
                                            }}>
                                                <CardContent>
                                                    <Typography variant="h6" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue' }}>{`${key} ${reportData.reportName} Activity`}</Typography>
                                                    <Divider style={{ background: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue' }} />
                                                    <br />
                                                    <Grid container spacing={1}>
                                                        <Grid item sm={6}>
                                                            <Typography gutterBottom variant="body1" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue', fontSize: 14 }}>
                                                                Duration
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item sm={6}>
                                                            <Typography gutterBottom variant="h6" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue' }}>
                                                                {`${reportData.meta.activeDuration[key]}`}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item sm={6}>
                                                            <Typography gutterBottom variant="body1" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue', fontSize: 14 }}>
                                                                Distance
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item sm={6}>
                                                            <Typography gutterBottom variant="h6" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue' }}>
                                                                {`${reportData.meta.activeDistance[key].toFixed(2)} Km`}
                                                            </Typography>
                                                        </Grid>
                                                        {
                                                            key === "Total" &&
                                                            <Fragment>
                                                                <Grid item sm={6}>
                                                                    <Typography gutterBottom variant="body1" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue', fontSize: 14 }}>
                                                                        Inactive
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item sm={6}>
                                                                    <Typography gutterBottom variant="h6" style={{ color: key === "Total" ? 'DarkOrchid' : index % 2 === 0 ? 'DodgerBlue' : 'RoyalBlue' }}>
                                                                        {`${reportData.meta.inactiveDuration}`}
                                                                    </Typography>
                                                                </Grid>
                                                            </Fragment>
                                                        }
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )
                                    :
                                    <Grid item container sm={6}>
                                        <Card style={{ width: '100%', borderColor: 'DodgerBlue', borderStyle: 'solid', borderWidth: '1px' }}>
                                            <CardContent>
                                                <Typography variant="h6" style={{ color: 'DodgerBlue' }}>{`${reportData.reportName} Activity`}</Typography>
                                                <Divider style={{ background: 'DodgerBlue' }} />
                                                <br />
                                                <Grid container spacing={1}>
                                                    <Grid item sm={6}>
                                                        <Typography gutterBottom variant="body1" style={{ color: 'DodgerBlue', fontSize: 14 }}>
                                                            Duration
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sm={6}>
                                                        <Typography gutterBottom variant="h6" style={{ color: 'DodgerBlue' }}>
                                                            {`${reportData.meta.activeDuration.Total}`}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sm={6}>
                                                        <Typography gutterBottom variant="body1" style={{ color: 'DodgerBlue', fontSize: 14 }}>
                                                            Distance
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sm={6}>
                                                        <Typography gutterBottom variant="h6" style={{ color: 'DodgerBlue' }}>
                                                            {`${reportData.meta.activeDistance.Total.toFixed(2)} Km`}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sm={6}>
                                                        <Typography gutterBottom variant="body1" style={{ color: 'DodgerBlue', fontSize: 14 }}>
                                                            {reportName && reportName.indexOf("Wire") != -1 ? "Good " : "Inactive "}Duration
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sm={6}>
                                                        <Typography gutterBottom variant="h6" style={{ color: 'DodgerBlue' }}>
                                                            {`${reportData.meta.inactiveDuration}`}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                            }

                            <Grid item sm={12} container>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} {...getTableProps()}>
                                        <TableHead>
                                            {headerGroups.map(headerGroup => (
                                                <TableRow {...headerGroup.getHeaderGroupProps()}>
                                                    {headerGroup.headers.map(column => (
                                                        <StyledTableCell
                                                            {...column.getHeaderProps()}
                                                        >
                                                            <b>{column.render('Header')}</b>
                                                        </StyledTableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableHead>
                                        <TableBody {...getTableBodyProps()}>
                                            {rows.map((row) => {
                                                prepareRow(row)
                                                return (
                                                    <StyledTableRow {...row.getRowProps()}>
                                                        {row.cells.map(cell => {
                                                            return (
                                                                <StyledTableCell
                                                                    {...cell.getCellProps()}
                                                                >
                                                                    {cell.render('Cell')}
                                                                </StyledTableCell>
                                                            )
                                                        })}
                                                    </StyledTableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            </Paper>
        );
    } else {
        return null;
    }


}

export default BinarySensor;
